import { useCallback, useRef, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { useDebounce } from '@uidotdev/usehooks';
import { toast as message } from 'sonner';
import { encode } from '@supabase-cache-helpers/postgrest-react-query';

import { getTables, buildQueryForFieldDependentTable } from '../../../utils/metadata/tables';
import { updateRecord, handleOperationError } from '../../../utils/supabase-operations';
import { renderWithHandlebars } from '../../admin/EmailTemplates/utils/templateRenderer';
import { trimEmail, isValidEmail } from '../../../utils/search';
import {
  claimFieldUpdates,
  removeClaimedFieldValues,
  SerializedTaskQueue
} from '../../../utils/recordWriteQueue';
import { toFormFieldValue } from '../utils/fieldValueUtils';

import type { EnhancedFormFieldValues, RecordFormMetadata } from '../../../types/record-form';
import type { DatabaseDataType } from '../../../types/record-form';
import type { FormFieldValue } from '../../../types/forms';

export interface WarningState {
  isOpen: boolean;
  message: string;
  title: string;
  confirmText: string;
  cancelText: string;
  pendingUpdate: {
    fieldName: string;
    value: FormFieldValue;
    previousValue?: FormFieldValue;
  } | null;
}

interface UseFieldUpdateHandlersParams {
  tableName: string;
  schemaName: string;
  recordId: number;
  getRecordArgs: any;
  processedMetadata: RecordFormMetadata;
  fieldValues: EnhancedFormFieldValues;
  setFieldValues: Dispatch<SetStateAction<EnhancedFormFieldValues>>;
  setFieldErrors: Dispatch<SetStateAction<Record<string, string | null>>>;
  debouncedFieldValues: Record<string, FormFieldValue>;
  setDebouncedFieldValues: Dispatch<SetStateAction<Record<string, FormFieldValue>>>;
  debouncedPreviousValues: Record<string, FormFieldValue>;
  setDebouncedPreviousValues: Dispatch<SetStateAction<Record<string, FormFieldValue>>>;
  fieldsBeingSaved: Set<string>;
  setFieldsBeingSaved: Dispatch<SetStateAction<Set<string>>>;
}

interface UseFieldUpdateHandlersResult {
  warningState: WarningState;
  confirmWarning: () => void;
  cancelWarning: () => void;
  handleFieldUpdate: (
    fieldName: string,
    value: FormFieldValue,
    previousValue?: FormFieldValue,
    skipWarning?: boolean
  ) => Promise<void>;
  debouncedHandleFieldUpdate: (fieldName: string, value: FormFieldValue) => void;
  debouncedEmailUpdate: (fieldName: string, value: string) => void;
  debouncedFieldValues: Record<string, FormFieldValue>;
  fieldsBeingSaved: Set<string>;
}

interface PendingFieldUpdate {
  fieldName: string;
  value: FormFieldValue;
  previousValue?: FormFieldValue;
}

export const useFieldUpdateHandlers = ({
  tableName,
  schemaName,
  recordId,
  getRecordArgs,
  processedMetadata,
  fieldValues,
  setFieldValues,
  setFieldErrors,
  debouncedFieldValues,
  setDebouncedFieldValues,
  debouncedPreviousValues,
  setDebouncedPreviousValues,
  fieldsBeingSaved,
  setFieldsBeingSaved
}: UseFieldUpdateHandlersParams): UseFieldUpdateHandlersResult => {
  const [warningState, setWarningState] = useState<WarningState>({
    isOpen: false,
    message: '',
    title: 'Confirm Change',
    confirmText: 'Yes',
    cancelText: 'Cancel',
    pendingUpdate: null
  });
  const saveQueueRef = useRef(new SerializedTaskQueue());

  const persistFieldUpdates = useCallback(async (updates: PendingFieldUpdate[]): Promise<void> => {
    const writableUpdates = updates.filter(({ fieldName }) => !fieldName.endsWith('_calc'));
    if (writableUpdates.length === 0) return;

    const fieldNames = writableUpdates.map(({ fieldName }) => fieldName);
    setFieldsBeingSaved(prev => new Set([...prev, ...fieldNames]));

    try {
      const patch: Record<string, FormFieldValue> = {};
      const expectedValues: Record<string, FormFieldValue> = {};
      const expectedValueTypes: Record<string, string> = {};

      for (const { fieldName, value, previousValue } of writableUpdates) {
        let updatedValue = value;
        const fieldDef = processedMetadata.fields?.[fieldName];
        const picklistConfig = processedMetadata.picklists?.[fieldName];

        if (picklistConfig?.type === 'Multi Select') {
          updatedValue = Array.isArray(value) && value.length > 0 ? JSON.stringify(value) : null;
        }

        if (fieldDef) {
          const dataType = fieldDef.data_type as DatabaseDataType;
          switch (dataType) {
            case 'boolean':
              updatedValue = !!value;
              break;
            case 'numeric':
            case 'integer':
            case 'bigint':
            case 'decimal':
              updatedValue = value ? parseFloat(value as string) : null;
              break;
            case 'date':
            case 'timestamp without time zone':
            case 'timestamp with time zone':
              updatedValue = value ? new Date(value as string | Date) : null;
              break;
          }
        }

        patch[fieldName] = updatedValue;
        if (previousValue !== undefined) {
          expectedValues[fieldName] = picklistConfig?.type === 'Multi Select'
            ? (Array.isArray(previousValue) && previousValue.length > 0 ? JSON.stringify(previousValue) : null)
            : previousValue ?? null;
          if (fieldDef?.data_type) expectedValueTypes[fieldName] = fieldDef.data_type;
        }
      }

      const updateResult = await updateRecord({
        tableName,
        schema: schemaName,
        data: patch,
        whereClause: { id: recordId },
        options: {
          invalidateCache: false,
          fieldName: fieldNames[0],
          ...(Object.keys(expectedValues).length > 0 ? { expectedValues, expectedValueTypes } : {})
        }
      });
      if (updateResult.error) throw updateResult.error;

      try {
        // Refresh the record query the form reads before the field leaves
        // `fieldsBeingSaved`; reconciliation then sees the written row. That
        // query's key carries the table's view columns, so rebuild it from the
        // same table metadata. A key built without them names a query nobody
        // observes: the form kept the pre-write row, reverted the field to it,
        // and the next write's stale-write guard reported a conflict.
        const { data: tableData, error: tableError } = await getTables();
        if (tableError) throw tableError;
        const { query } = buildQueryForFieldDependentTable({
          ...getRecordArgs,
          tableInfo: tableData?.byName?.[tableName],
          schema: schemaName,
          deep: true
        });
        await window.queryClient.refetchQueries({ queryKey: encode(query, false), exact: true });
      } catch (refreshError) {
        console.error(`Error refreshing ${tableName}:`, refreshError);
        message.error(`Failed to refresh ${tableName}: ${(refreshError as Error).message}`, { duration: 7000 });
      }
    } catch (error) {
      // Do not overwrite a newer optimistic edit that is queued behind this batch.
      setFieldValues((prev) => {
        const next = { ...prev } as EnhancedFormFieldValues;
        for (const { fieldName, value, previousValue } of writableUpdates) {
          if (Object.is(prev[fieldName], value)) next[fieldName] = previousValue;
        }
        return next;
      });

      const errorInfo = handleOperationError(error, `Failed to update ${fieldNames.join(', ')}`);
      message.error(errorInfo.userMessage);
      console.error(`Error updating ${tableName}.${fieldNames.join(',')}:`, errorInfo.debugInfo);
    } finally {
      setFieldsBeingSaved(prev => {
        const next = new Set(prev);
        fieldNames.forEach(fieldName => next.delete(fieldName));
        return next;
      });
    }
  }, [getRecordArgs, processedMetadata.fields, processedMetadata.picklists, recordId, schemaName, setFieldValues, setFieldsBeingSaved, tableName]);

  const handleFieldUpdate = useCallback(async (
    fieldName: string,
    value: FormFieldValue,
    previousValue?: FormFieldValue,
    skipWarning: boolean = false
  ): Promise<void> => {
    try {
      // Skip updates for calculated fields
      if (fieldName.endsWith('_calc')) {
        return;
      }

      // Check for warning message configuration
      const fieldDef = processedMetadata.fields?.[fieldName];
      const warningConfig = (fieldDef?.ui_data as any)?.warning_message;
      if (warningConfig?.enabled && !skipWarning) {
        // Don't warn if value hasn't effectively changed
        if (value === previousValue) return;

        try {
          // Prepare context for template
          const templateContext = {
            oldValue: previousValue,
            newValue: value,
            field_label: fieldDef?.ui_label || fieldName,
            record: fieldValues // Give access to full record context
          };

          // Render message
          const warningMessage = await renderWithHandlebars(warningConfig.message, templateContext);
          const title = await renderWithHandlebars(warningConfig.title || 'Confirm Change', templateContext);

          setWarningState({
            isOpen: true,
            message: warningMessage,
            title,
            confirmText: warningConfig.confirmText || 'Yes',
            cancelText: warningConfig.cancelText || 'Cancel',
            pendingUpdate: {
              fieldName,
              value,
              previousValue
            }
          });
          return; // Stop here, wait for modal
        } catch (err) {
          console.error('Error rendering warning message:', err);
          // Proceeding to avoid blocking user on template error
        }
      }

      setFieldValues((prev) => ({ ...prev, [fieldName]: value } as EnhancedFormFieldValues));
      // Writes to one record run in order. Two quick edits to the same field
      // would otherwise race, and the second one's guard (the first one's
      // result) fails when it lands first.
      await saveQueueRef.current.enqueue(() => persistFieldUpdates([{ fieldName, value, previousValue }]));
    } catch (error) {
      console.error('Error updating record:', error);
      message.error(`Failed to update ${tableName}: ${(error as Error).message}`, { duration: 7000 });
    }
  }, [fieldValues, persistFieldUpdates, processedMetadata.fields, setFieldValues, tableName]);

  const debouncedHandleFieldUpdate = useCallback((fieldName: string, value: FormFieldValue): void => {
    // Capture the server value once per debounce window. Checking the value's
    // truthiness breaks null/zero numeric fields: every keystroke replaces the
    // baseline with the user's preceding keystroke, so the stale-write guard
    // reports a conflict even when nobody else touched the record.
    setDebouncedPreviousValues((prev) => (
      Object.prototype.hasOwnProperty.call(prev, fieldName)
        ? prev
        : { ...prev, [fieldName]: toFormFieldValue(fieldValues[fieldName]) }
    ));

    // Immediately update UI for responsive feedback
    setFieldValues((prev) => ({ ...prev, [fieldName]: value } as EnhancedFormFieldValues));

    // Queue debounced server update
    setDebouncedFieldValues((prev) => {
      return { ...prev, [fieldName]: value };
    });
  }, [fieldValues, setDebouncedFieldValues, setDebouncedPreviousValues, setFieldValues]);

  const debouncedEmailUpdate = useCallback((fieldName: string, value: string): void => {
    const trimmed = trimEmail(value);

    if (trimmed && !isValidEmail(trimmed)) {
      setFieldErrors((prev) => ({ ...prev, [fieldName]: 'Invalid email format' }));
      return;
    }

    setFieldErrors((prev) => ({ ...prev, [fieldName]: null }));
    debouncedHandleFieldUpdate(fieldName, trimmed);
  }, [debouncedHandleFieldUpdate, setFieldErrors]);

  useDebounce(() => {
    const updates = claimFieldUpdates(debouncedFieldValues, debouncedPreviousValues);
    if (updates.length === 0) return;

    // Claim this exact snapshot before starting I/O. Keeping it in React state until
    // a slow request completed allowed rerenders to schedule the same save repeatedly.
    setDebouncedFieldValues((pending) => removeClaimedFieldValues(pending, updates));
    setDebouncedPreviousValues((pending) => {
      const next = { ...pending };
      for (const { fieldName } of updates) delete next[fieldName];
      return next;
    });

    // A debounce window becomes one PATCH and one refresh. Later windows wait for
    // the current record write so edits cannot create tuple-lock contention.
    const confirmedUpdates: PendingFieldUpdate[] = [];
    const warningUpdates: PendingFieldUpdate[] = [];

    for (const update of updates) {
      const warningConfig = (processedMetadata.fields?.[update.fieldName]?.ui_data as any)?.warning_message;
      (warningConfig?.enabled ? warningUpdates : confirmedUpdates).push(update);
    }

    // handleFieldUpdate queues its own write, so it must not run inside a
    // queued task: that task would wait on a write queued behind itself.
    void (async () => {
      if (confirmedUpdates.length > 0) {
        await saveQueueRef.current.enqueue(() => persistFieldUpdates(confirmedUpdates));
      }
      for (const update of warningUpdates) {
        await handleFieldUpdate(update.fieldName, update.value, update.previousValue);
      }
    })();
  }, 750);

  const confirmWarning = useCallback(() => {
    if (warningState.pendingUpdate) {
      const { fieldName, value, previousValue } = warningState.pendingUpdate;
      handleFieldUpdate(fieldName, value, previousValue, true);
      setWarningState(prev => ({ ...prev, isOpen: false, pendingUpdate: null }));
    }
  }, [warningState.pendingUpdate, handleFieldUpdate]);

  const cancelWarning = useCallback(() => {
    // Revert UI if needed (especially for debounced fields that optimistically updated)
    if (warningState.pendingUpdate) {
      const { fieldName, previousValue } = warningState.pendingUpdate;
      setFieldValues((prev) => ({ ...prev, [fieldName]: previousValue } as EnhancedFormFieldValues));

      // Also clear any pending debounced updates for this field
      setDebouncedFieldValues(prev => {
        const next = { ...prev };
        delete next[fieldName];
        return next;
      });
    }
    setWarningState(prev => ({ ...prev, isOpen: false, pendingUpdate: null }));
  }, [setDebouncedFieldValues, setFieldValues, warningState.pendingUpdate]);

  return {
    warningState,
    confirmWarning,
    cancelWarning,
    handleFieldUpdate,
    debouncedHandleFieldUpdate,
    debouncedEmailUpdate,
    debouncedFieldValues,
    fieldsBeingSaved
  };
};
